/**
 * Mirrors backend `app.utils.contact_normalization` for client-side checks.
 */

import { isPossiblePhoneNumber } from "libphonenumber-js";
import parsePhoneNumber from "libphonenumber-js";

const MIN_PHONE_DIGITS = 9;
const MAX_E164_DIGITS = 15;
const E164_BODY = /^\+[1-9]\d{8,14}$/;

export function isStructurallyValidE164(value: string): boolean {
  if (!E164_BODY.test(value)) return false;
  const digits = value.slice(1);
  return (
    digits.length >= MIN_PHONE_DIGITS &&
    digits.length <= MAX_E164_DIGITS &&
    /^\d+$/.test(digits)
  );
}

/** Return canonical E.164 or undefined when the value cannot be normalized. */
export function normalizePhone(
  phone: string | null | undefined,
): string | undefined {
  if (!phone) return undefined;
  const raw = phone.trim();
  if (!raw) return undefined;

  let candidate = raw;
  if (candidate.startsWith("00")) {
    candidate = `+${candidate.slice(2)}`;
  }
  if (!candidate.startsWith("+")) {
    return undefined;
  }

  const parsed = parsePhoneNumber(candidate);
  if (!parsed?.isPossible()) {
    return undefined;
  }

  const e164 = parsed.format("E.164");
  if (!isStructurallyValidE164(e164)) {
    return undefined;
  }
  return e164;
}

/** True when ``raw`` normalizes to a valid E.164 string within ``maxStoredLen``. */
export function isValidPhone(
  raw: string,
  maxStoredLen = 20,
): boolean {
  const normalized = normalizePhone(raw);
  if (!normalized || normalized.length > maxStoredLen) {
    return false;
  }
  return isPossiblePhoneNumber(normalized);
}
